/**
 * Shared matching for a saved price-template rule → a platform price field.
 * Used by Listing Plan (AddNewPriceTemplateModal) and Listing Profile hydrate.
 */
export type PriceTemplateMatchField = {
  id: number
  platform_field?: number | null
  api_id?: string | number | null
  api_code?: string | null
  api_name?: string | null
}

const sameId = (left: unknown, right: unknown): boolean => {
  if (left == null || left === '' || right == null || right === '') return false
  return String(left) === String(right)
}

export const getPriceRuleApiIdentity = (rule: any) => ({
  apiId:
    rule?.destination_platform_field?.api_id ||
    rule?.destination_platform_field_api_id ||
    rule?.api_id,
  apiCode:
    rule?.destination_platform_field?.api_code ||
    rule?.destination_platform_field_api_code ||
    rule?.api_code,
  apiName:
    rule?.destination_platform_field?.api_name ||
    rule?.destination_platform_field_api_name ||
    rule?.api_name,
})

/** Price-field row id: GET /platform-price-template uses destination_platform_field_data. */
export const getPriceRuleFieldDataId = (rule: any): unknown =>
  rule?.platform_price_template_field ??
  rule?.destination_platform_field_data ??
  rule?.destination_platform_field_data_id

/** Parent platform field id: number or nested object from either API shape. */
export const getPriceRulePlatformFieldId = (rule: any): unknown => {
  if (typeof rule?.destination_platform_field === 'number') {
    return rule.destination_platform_field
  }
  return (
    rule?.destination_platform_field_id ??
    rule?.destination_platform_field?.id ??
    null
  )
}

export const getPriceRuleDestinationId = (rule: any): unknown =>
  getPriceRuleFieldDataId(rule) ?? getPriceRulePlatformFieldId(rule)

export const matchPriceRuleToField = <T extends PriceTemplateMatchField>(
  rule: any,
  fields: readonly T[]
): T | undefined => {
  if (!rule || !fields.length) return undefined

  const fieldDataId = getPriceRuleFieldDataId(rule)
  if (fieldDataId != null) {
    const byFieldData = fields.find((field) => sameId(field.id, fieldDataId))
    if (byFieldData) return byFieldData
  }

  const { apiId, apiCode, apiName } = getPriceRuleApiIdentity(rule)
  const byApi = fields.find(
    (field) =>
      sameId(field.api_id, apiId) ||
      sameId(field.api_code, apiCode) ||
      sameId(field.api_name, apiName)
  )
  if (byApi) return byApi

  const platformFieldId = getPriceRulePlatformFieldId(rule)
  if (platformFieldId == null) return undefined

  const candidates = fields.filter(
    (field) =>
      sameId(field.platform_field, platformFieldId) || sameId(field.id, platformFieldId)
  )
  if (candidates.length === 1) return candidates[0]
  return undefined
}
